import React, { useEffect, useState } from 'react';
import { fetchShippingMethodGroups, fetchSpecialCharges } from '../services/api';
import { EditDialog, Field, ListCard, PageTitle, WriteOffNote, useRows, useSave, useWriteLive } from '../components/StaffList';

/**
 * Tools & Lists > Special Charges: extra shipping fees for chosen dates and shipping methods, such as
 * Holiday Assurance (Azure exit WS8). Reads sql01.SpecialShippingCharges. Saving is off until
 * ws8_special_charges_live. A shipping type holds several method ids and the table keeps the ids, so
 * a type shows ticked when all its ids are on the charge.
 */

const showDate = (ymd) => { const [y, m, d] = ymd.split('-'); return `${m}/${d}/${y}`; };
const FEE = /^[0-9]+(\.[0-9]{1,2})?$/;
const idList = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
const empty = { id: 0, name: '', startDate: '', endDate: '', effectiveStartDate: '', effectiveEndDate: '', additionalFee: '', zeroFee: '', methods: [] };

export default function SpecialCharges() {
  const live = useWriteLive('ws8_special_charges');
  const [f, setF] = useState({ name: '', startDate: '', endDate: '', additionalFee: '', zeroFee: '' });
  const [groups, setGroups] = useState([]);
  const [edit, setEdit] = useState(null);
  const { rows, error, reload } = useRows(() => fetchSpecialCharges(f), [f]);
  const { save, saving, error: saveError, setError } = useSave(reload);

  useEffect(() => { fetchShippingMethodGroups().then(setGroups).catch(() => {}); }, []);

  const set = (key, value) => setF((x) => ({ ...x, [key]: value }));
  const field = (key, label, type = 'text') => (
    <Field label={label}><input type={type} className="form-input" value={edit[key]} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} /></Field>
  );
  const ticked = (g) => idList(g.ids).every((id) => edit.methods.includes(id));
  const toggle = (g, on) => {
    const ids = idList(g.ids);
    setEdit({ ...edit, methods: on ? [...edit.methods, ...ids.filter((x) => !edit.methods.includes(x))] : edit.methods.filter((x) => !ids.includes(x)) });
  };
  const toggleAll = (on) => setEdit({ ...edit, methods: on ? groups.flatMap((g) => idList(g.ids)) : [] });

  const submit = async (saveOverlap = false) => {
    const e = edit;
    if (!e.name.trim() || !e.startDate || !e.endDate || !e.effectiveStartDate || !e.effectiveEndDate || !FEE.test(e.additionalFee) || !FEE.test(e.zeroFee)) {
      return setError('Please enter all required fields.');
    }
    const out = await save('special_charge_save', { ...e, saveOverlap });
    if (out && out.overlap) {
      // eslint-disable-next-line no-alert
      if (window.confirm(`This charge overlaps with the ${out.overlap} charge, Are you sure you want to do this?`)) return submit(true);
      return null;
    }
    if (out) setEdit(null);
    return null;
  };
  const remove = (r) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Do you want to delete?')) save('special_charge_delete', { id: r.id });
  };

  return (
    <div>
      <PageTitle>Special Charges</PageTitle>
      <WriteOffNote live={live} />
      <div className="port-filters">
        <input className="form-input" style={{ width: 'auto' }} placeholder="Name" aria-label="Name" value={f.name} onChange={(e) => set('name', e.target.value)} />
        <input type="date" className="form-input" style={{ width: 'auto' }} aria-label="Charge start date" value={f.startDate} onChange={(e) => set('startDate', e.target.value)} />
        <input type="date" className="form-input" style={{ width: 'auto' }} aria-label="Charge end date" value={f.endDate} onChange={(e) => set('endDate', e.target.value)} />
        <input className="form-input" style={{ width: 'auto' }} placeholder="Additional fee" aria-label="Additional shipping costs fee" value={f.additionalFee} onChange={(e) => set('additionalFee', e.target.value)} />
        <input className="form-input" style={{ width: 'auto' }} placeholder="Zero shipping fee" aria-label="Zero shipping costs fee" value={f.zeroFee} onChange={(e) => set('zeroFee', e.target.value)} />
        <button type="button" className="btn btn-primary" disabled={!live} onClick={() => { setError(null); setEdit(empty); }}>Add Special Charges</button>
      </div>
      <ListCard title="Special charges" rows={rows} error={error} empty="No special charges.">
        <table className="trans-table">
          <thead><tr><th>Name</th><th>Charge Start Date</th><th>Charge End Date</th><th>Effective Start Date</th><th>Effective End Date</th><th>Additional Shipping Costs Fee</th><th>Zero Shipping Costs Fee</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td>{showDate(r.startDate)}</td>
                <td>{showDate(r.endDate)}</td>
                <td>{showDate(r.effectiveStartDate)}</td>
                <td>{showDate(r.effectiveEndDate)}</td>
                <td>{r.additionalFee}</td>
                <td>{r.zeroFee}</td>
                <td className="staff-actions">
                  <button type="button" className="btn btn-ghost" disabled={!live || saving} onClick={() => { setError(null); setEdit({ ...r, methods: idList(r.methods) }); }}>Edit</button>
                  <button type="button" className="btn btn-ghost" disabled={!live || saving} onClick={() => remove(r)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListCard>
      {!edit && saveError && <div className="staff-msg staff-msg-error">{saveError}</div>}
      {edit && (
        <EditDialog title={edit.id ? 'Edit Special Charges' : 'Add Special Charges'} live={live} saving={saving} error={saveError} onClose={() => setEdit(null)} onSave={() => submit()}>
          {field('name', 'Name')}
          <div className="staff-grid">
            {field('startDate', 'Charge Start Date', 'date')}
            {field('endDate', 'Charge End Date', 'date')}
            {field('effectiveStartDate', 'Effective Start Date', 'date')}
            {field('effectiveEndDate', 'Effective End Date', 'date')}
            {field('additionalFee', 'Additional Shipping Costs Fee')}
            {field('zeroFee', 'Zero Shipping Costs Fee')}
          </div>
          <Field label="Shipping Methods">
            <div className="staff-grid">
              <label className="staff-check"><input type="checkbox" checked={groups.length > 0 && groups.every(ticked)} onChange={(e) => toggleAll(e.target.checked)} /> All</label>
              {groups.map((g) => <label key={g.label} className="staff-check"><input type="checkbox" checked={ticked(g)} onChange={(e) => toggle(g, e.target.checked)} /> {g.label}</label>)}
            </div>
          </Field>
        </EditDialog>
      )}
    </div>
  );
}
